// Every backend endpoint the app calls, relative to appConfig.api.baseURL.
// Add new APIs here and call them through services/api.js, never with hard-coded URLs.
// IDs are always URL-encoded so a value can't change which endpoint is called.

const seg = (value) => {
  if (value === undefined || value === null || value === '') throw new Error('API path parameter is missing');
  return encodeURIComponent(String(value));
};

// Standard REST paths for a collection: list/create at `base`, item at `base/:id`.
const resource = (base) => ({
  list: base,
  create: base,
  byId: (id) => `${base}/${seg(id)}`,
  update: (id) => `${base}/${seg(id)}`,
  remove: (id) => `${base}/${seg(id)}`,
});

export const API = Object.freeze({
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',

    
    refresh: '/auth/refresh', // uses the httpOnly refresh-token cookie
    me: '/auth/me',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    verifyEmail: '/auth/verify-email',
    resendVerification: '/auth/resend-verification',
    changePassword: '/auth/change-password',
  },

  profile: {
    me: '/profile',
    update: '/profile',
    avatar: '/profile/avatar',
    notificationPreferences: '/profile/notification-preferences',
  },

  public: {
    searchJourneys: '/public/journeys/search',
    page: (slug) => `/public/pages/${seg(slug)}`,
    faqs: '/public/faqs',
    prohibitedItems: '/public/prohibited-items',
    categories: '/public/categories',
    priceEstimate: '/public/price-estimate',
    contact: '/public/contact',
  },

  masterData: {
    countries: resource('/master/countries'),
    states: resource('/master/states'),
    cities: resource('/master/cities'),
    stations: resource('/master/stations'),
    transportModes: resource('/master/transport-modes'),
    categories: resource('/master/categories'),
    prohibitedItems: resource('/master/prohibited-items'),
  },

  sender: {
    dashboard: '/sender/dashboard',
    searchTravellers: '/sender/travellers/search',
    travellerProfile: (journeyId) => `/sender/journeys/${seg(journeyId)}/traveller`,
    requests: resource('/sender/requests'),
    cancelRequest: (id) => `/sender/requests/${seg(id)}/cancel`,
    assignment: (id) => `/sender/requests/${seg(id)}/assignment`,
    assignedTraveller: (id) => `/sender/requests/${seg(id)}/traveller`,
    handover: (id) => `/sender/requests/${seg(id)}/handover`,
    delivery: (id) => `/sender/requests/${seg(id)}/delivery`,
    createPayment: (id) => `/sender/requests/${seg(id)}/payments`,
    verifyPayment: (id) => `/sender/payments/${seg(id)}/verify`,
    payments: '/sender/payments',
    receipt: (paymentId) => `/sender/payments/${seg(paymentId)}/receipt`,
  },

  traveller: {
    dashboard: '/traveller/dashboard',
    journeys: resource('/traveller/journeys'),
    publishJourney: (id) => `/traveller/journeys/${seg(id)}/publish`,
    cancelJourney: (id) => `/traveller/journeys/${seg(id)}/cancel`,
    verification: '/traveller/verification',
    verificationDocuments: '/traveller/verification/documents',
    requests: resource('/traveller/requests'),
    confirmAssignment: (id) => `/traveller/requests/${seg(id)}/confirm`,
    declineAssignment: (id) => `/traveller/requests/${seg(id)}/decline`,
    senderDetails: (id) => `/traveller/requests/${seg(id)}/sender`,
    confirmHandover: (id) => `/traveller/requests/${seg(id)}/handover`,
    startTransit: (id) => `/traveller/requests/${seg(id)}/in-transit`,
    confirmDelivery: (id) => `/traveller/requests/${seg(id)}/delivery`,
    earnings: '/traveller/earnings',
  },

  // Shared by senders and travellers; the backend scopes results to the signed-in user.
  conversations: {
    list: '/conversations',
    byRequest: (requestId) => `/conversations/${seg(requestId)}`,
    messages: (requestId) => `/conversations/${seg(requestId)}/messages`,
  },
  notifications: {
    list: '/notifications',
    markRead: (id) => `/notifications/${seg(id)}/read`,
    markAllRead: '/notifications/read-all',
  },
  reviews: {
    list: '/reviews',
    pending: '/reviews/pending',
    create: '/reviews',
  },
  support: {
    tickets: resource('/support/tickets'),
  },

  transportModes: {
    getAll: '/TransportMode/GetAll',
    getActive: '/TransportMode/GetActiveList',
    byId: (id) => `/TransportMode/${encodeURIComponent(String(id))}`,
    save: '/TransportMode', // POST for both create and update
    delete: (id) => `/TransportMode/${encodeURIComponent(String(id))}`,
  },

  packageCategories: {
    getAll: '/PackageCategory/GetAll',
    getActive: '/PackageCategory/GetActiveList',
    byId: (id) => `/PackageCategory/${seg(id)}`,
    save: '/PackageCategory', // POST for both create and update
    delete: (id) => `/PackageCategory/${seg(id)}`,
  },

  // byId accepts the profile id or the user id (the backend matches either).
  senderProfiles: {
    getAll: '/SenderProfile/GetAll',
    byId: (id) => `/SenderProfile/${seg(id)}`,
    save: '/SenderProfile', // POST for both create and update
    delete: (id) => `/SenderProfile/${seg(id)}`,
  },

  travellerProfiles: {
    getAll: '/TravellerProfile/GetAll',
    byId: (id) => `/TravellerProfile/${seg(id)}`,
    save: '/TravellerProfile', // POST for both create and update
    delete: (id) => `/TravellerProfile/${seg(id)}`,
  },

  travellerJourneys: {
    getAll: '/TravellerJourney/GetAll', // ?source=&destination=
    byId: (id) => `/TravellerJourney/${seg(id)}`,
    byTraveller: (travellerId) => `/TravellerJourney/GetByTraveller/${seg(travellerId)}`,
    save: '/TravellerJourney', // POST for both create and update
    delete: (id) => `/TravellerJourney/${seg(id)}`,
  },

  prohibitedItems: {
    getAll: '/ProhibitedItem/GetAll',
    getActive: '/ProhibitedItem/GetActiveList', // public
    byId: (id) => `/ProhibitedItem/${seg(id)}`,
    save: '/ProhibitedItem',
    delete: (id) => `/ProhibitedItem/${seg(id)}`,
  },

  // Delivery flow: assignment → handovers (OTP) → payment → review. All scoped by role on the server.
  assignments: {
    getAll: '/TravellerAssignment/GetAll',
    byId: (id) => `/TravellerAssignment/${seg(id)}`,
    candidates: (packageRequestId) => `/TravellerAssignment/Candidates/${seg(packageRequestId)}`, // admin
    save: '/TravellerAssignment', // admin assigns / re-assigns
    respond: (id) => `/TravellerAssignment/${seg(id)}/respond`, // traveller { accept, notes }
    updateStatus: (id) => `/TravellerAssignment/${seg(id)}/status`, // admin ?status=
    delete: (id) => `/TravellerAssignment/${seg(id)}`,
    select: '/TravellerAssignment/Select', // sender picks a journey { packageRequestId, travellerJourneyId }
    removeSelection: (id) => `/TravellerAssignment/${seg(id)}/selection`, // sender, unpaid only
    approve: (id) => `/TravellerAssignment/${seg(id)}/approve`, // admin approves a paid selection
    quote: '/TravellerAssignment/Quote', // ?packageRequestId=&travellerJourneyId= → traveller price vs offer, luggage fee + platform fee
    setLuggageFee: (id) => `/TravellerAssignment/${seg(id)}/luggage-fee`, // admin closes a price negotiation { luggageFee, note }
  },

  // Public traveller search: masked identity, route, date, mode, free capacity
  journeySearch: {
    search: '/TravellerJourney/Search',
    publicById: (id) => `/TravellerJourney/Public/${seg(id)}`,
  },

  notificationCenter: {
    mine: '/Notification/Mine',
    unreadCount: '/Notification/UnreadCount',
    read: (id) => `/Notification/${seg(id)}/read`,
    readAll: '/Notification/read-all',
    delete: (id) => `/Notification/${seg(id)}`,
    broadcasts: '/Notification/Broadcasts', // admin
    broadcast: '/Notification/Broadcast', // admin
  },

  chat: {
    conversations: '/Chat/Conversations',
    unreadCount: '/Chat/UnreadCount',
    messages: (assignmentId) => `/Chat/${seg(assignmentId)}`, // GET ?after=, POST { message }
  },

  helpdesk: {
    tickets: '/Support/Tickets',
    create: '/Support/Ticket',
    ticket: (id) => `/Support/Ticket/${seg(id)}`, // GET; PATCH (admin)
    reply: (id) => `/Support/Ticket/${seg(id)}/reply`,
    contact: '/Support/Contact', // public
    contacts: '/Support/Contacts', // admin
    contactById: (id) => `/Support/Contact/${seg(id)}`, // PATCH / DELETE (admin)
  },

  verification: {
    documentTypes: '/Verification/DocumentTypes',
    mine: '/Verification/Mine',
    upload: '/Verification/Upload',
    deleteDocument: (id) => `/Verification/Document/${seg(id)}`,
    file: (id) => `/Verification/Document/${seg(id)}/file`,
    queue: '/Verification/Queue', // admin
    byTraveller: (travellerId) => `/Verification/Traveller/${seg(travellerId)}`,
    bySender: (senderId) => `/Verification/Sender/${seg(senderId)}`, // admin
    reviewDocument: (id) => `/Verification/Document/${seg(id)}/review`,
    decide: (travellerId) => `/Verification/Traveller/${seg(travellerId)}/decision`,
  },

  // City search over Cities.json (public) ?q=&limit=
  places: {
    cities: '/Place/Cities',
  },

  locations: Object.fromEntries(['Country', 'State', 'City', 'Station'].map((name) => [name.toLowerCase(), {
    getAll: `/${name}/GetAll`,
    getActive: `/${name}/GetActiveList`,
    save: `/${name}`,
    delete: (id) => `/${name}/${seg(id)}`,
  }])),

  pricing: {
    getAll: '/Pricing/GetAll',
    estimate: '/Pricing/Estimate', // public ?transportModeId=&isInternational=&weightKg=
    save: '/Pricing',
    delete: (id) => `/Pricing/${seg(id)}`,
  },

  cms: {
    pages: '/Cms/Pages',
    page: (slug) => `/Cms/Page/${seg(slug)}`, // public, published only
    savePage: '/Cms/Page',
    deletePage: (id) => `/Cms/Page/${seg(id)}`,
    faqs: '/Cms/Faqs',
    publicFaqs: '/Cms/Faqs/Public',
    saveFaq: '/Cms/Faq',
    deleteFaq: (id) => `/Cms/Faq/${seg(id)}`,
  },

  analytics: {
    summary: '/Report/Summary',
    monthly: '/Report/Monthly',
    byMode: '/Report/ByMode',
    topRoutes: '/Report/TopRoutes',
    export: '/Report/Export', // ?type=requests|payments
  },

  dashboards: {
    sender: '/Dashboard/Sender',
    traveller: '/Dashboard/Traveller',
  },

  userAdmin: {
    getAll: '/UserManagement/GetAll', // ?role=Sender|Traveller|Admin
    status: (id) => `/UserManagement/${seg(id)}/status`,
    createAdmin: '/UserManagement/Admin',
  },

  settings: {
    getAll: '/SystemSetting/GetAll',
    public: '/SystemSetting/Public',
    update: '/SystemSetting',
  },

  handovers: {
    getAll: '/Handover/GetAll', // admin
    byAssignment: (assignmentId) => `/Handover/GetByAssignment/${seg(assignmentId)}`,
    save: '/Handover',
    verify: (id) => `/Handover/${seg(id)}/verify`, // traveller { otp }
    regenerateOtp: (id) => `/Handover/${seg(id)}/regenerate-otp`,
    delete: (id) => `/Handover/${seg(id)}`,
  },

  payments: {
    getAll: '/Payment/GetAll',
    byId: (id) => `/Payment/${seg(id)}`,
    createOrder: '/Payment/CreateOrder',
    verify: (id) => `/Payment/${seg(id)}/verify`,
    fail: (id) => `/Payment/${seg(id)}/fail`,
    release: (id) => `/Payment/${seg(id)}/release`, // admin
    delete: (id) => `/Payment/${seg(id)}`,
  },

  paymentTransactions: {
    getAll: '/PaymentTransaction/GetAll', // admin
    byPayment: (paymentId) => `/PaymentTransaction/GetByPayment/${seg(paymentId)}`,
  },

  refunds: {
    getAll: '/Refund/GetAll',
    save: '/Refund', // admin
    process: (id) => `/Refund/${seg(id)}/process`,
    delete: (id) => `/Refund/${seg(id)}`,
  },

  cancellations: {
    getAll: '/Cancellation/GetAll',
    create: '/Cancellation',
    decision: (id) => `/Cancellation/${seg(id)}/decision`, // admin { approve, cancellationPenalty }
    delete: (id) => `/Cancellation/${seg(id)}`,
  },

  ratingReviews: {
    getAll: '/RatingReview/GetAll',
    mine: '/RatingReview/Mine', // ?given=true for reviews I wrote
    pending: '/RatingReview/Pending',
    summary: (userId) => `/RatingReview/Summary/${seg(userId)}`,
    save: '/RatingReview',
    delete: (id) => `/RatingReview/${seg(id)}`,
  },

  // Backend scopes by role: senders get their own requests, travellers only APPROVED ones.
  packageRequests: {
    getAll: '/PackageRequest/GetAll',
    byId: (id) => `/PackageRequest/${seg(id)}`,
    save: '/PackageRequest', // POST for both create and update
    updateStatus: (id) => `/PackageRequest/${seg(id)}/status`, // admin, ?status=APPROVED
    cancel: (id) => `/PackageRequest/${seg(id)}/cancel`,
    delete: (id) => `/PackageRequest/${seg(id)}`,
  },


  admin: {
    dashboard: '/admin/dashboard',
    users: resource('/admin/users'),
    userStatus: (id) => `/admin/users/${seg(id)}/status`,
    senders: resource('/admin/senders'),
    travellers: resource('/admin/travellers'),
    verifications: resource('/admin/verifications'),
    verificationDecision: (travellerId) => `/admin/verifications/${seg(travellerId)}/decision`,
    journeys: resource('/admin/journeys'),
    requests: resource('/admin/requests'),
    assignmentCandidates: (requestId) => `/admin/requests/${seg(requestId)}/candidates`,
    assign: (requestId) => `/admin/requests/${seg(requestId)}/assign`,
    cancelRequest: (requestId) => `/admin/requests/${seg(requestId)}/cancel`,
    notifyParties: (requestId) => `/admin/requests/${seg(requestId)}/notify`,
    assignmentHistory: '/admin/assignments/history',
    payments: resource('/admin/payments'),
    cancellations: resource('/admin/cancellations'),
    cancellationDecision: (id) => `/admin/cancellations/${seg(id)}/decision`,
    refunds: resource('/admin/refunds'),
    processRefund: (id) => `/admin/refunds/${seg(id)}/process`,
    complaints: resource('/admin/complaints'),
    reviews: resource('/admin/reviews'),
    moderateReview: (id) => `/admin/reviews/${seg(id)}/moderation`,
    broadcasts: resource('/admin/notifications/broadcasts'),
    notificationTemplates: resource('/admin/notifications/templates'),
    pricing: resource('/admin/pricing-rules'),
    cmsPages: resource('/admin/cms/pages'),
    reports: {
      summary: '/admin/reports/summary',
      monthly: '/admin/reports/monthly',
      byMode: '/admin/reports/by-mode',
      topRoutes: '/admin/reports/top-routes',
      export: '/admin/reports/export',
    },
    admins: resource('/admin/admin-users'),
    settings: '/admin/settings',
  },
});

export default API;
